const toBool = (v: string | undefined): boolean => {
  if (!v) return false;
  return v === "1" || v === "true";
};

const toNumber = (v: string | undefined): number => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const toString = (v: string | undefined): string => v ?? "";

export const SYSTEM_CONFIG_REGISTRY = [
  {
    key: "app_name",
    label: "Application Name",
    description: "Name displayed in browser title and login page",
    category: "branding",
    defaultValue: "POS System",
    parse: toString,
    isPublic: true,
  },
  {
    key: "app_logo",
    label: "Application Logo",
    description: "Logo filename in /images folder (e.g., applogo.jpg)",
    category: "branding",
    defaultValue: "applogo.jpg",
    parse: toString,
    isPublic: true,
  },
  {
    key: "store_name",
    label: "Store Name",
    description: "Business name printed on receipts and invoices",
    category: "branding",
    defaultValue: "POS System",
    parse: toString,
    isPublic: true,
  },
  {
    key: "address",
    label: "Store Address",
    description: "Physical address printed on receipts",
    category: "branding",
    defaultValue: "",
    parse: toString,
    isPublic: true,
  },
  {
    key: "phone",
    label: "Store Phone",
    description: "Contact phone printed on receipts",
    category: "branding",
    defaultValue: "",
    parse: toString,
    isPublic: true,
  },
  {
    key: "email",
    label: "Store Email",
    description: "Contact email for receipts and communications",
    category: "branding",
    defaultValue: "",
    parse: toString,
    isPublic: true,
  },
  {
    key: "whatsapp",
    label: "WhatsApp Number",
    description: "WhatsApp contact for customer communication",
    category: "branding",
    defaultValue: "",
    parse: toString,
    isPublic: true,
  },
  {
    key: "website",
    label: "Website URL",
    description: "Business website printed on receipts",
    category: "branding",
    defaultValue: "",
    parse: toString,
    isPublic: true,
  },
  {
    key: "is_service",
    label: "Enable Service/Garage Features",
    description:
      "Enable repair jobs, service orders for garage/service businesses",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: true,
  },
  {
    key: "is_mobileshop",
    label: "Enable Mobile Shop Features",
    description: "Enable mobile repair, IMEI tracking for mobile shops",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: true,
  },
  {
    key: "is_restaurant",
    label: "Enable Restaurant Features",
    description:
      "Enable table management, KOT, kitchen display for restaurants",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: true,
  },
  {
    key: "is_pos_purchasing",
    label: "Enable POS Purchasing Mode",
    description: "Enable POS Purchasing Mode button in Quick Action",
    category: "features",
    defaultValue: true,
    parse: toBool,
    isPublic: true,
  },
  {
    key: "is_onlinestore_enabled",
    label: "Enable Online Store",
    description: "Enable online ordering and e-commerce features",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: true,
  },
  {
    key: "is_whatsapp_enabled",
    label: "Enable WhatsApp Integration",
    description: "Enable WhatsApp notifications and chatbot",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: true,
  },
  {
    key: "is_courier_enabled",
    label: "Enable Courier Features",
    description:
      "Enable courier delivery, parcel and courier assignment features",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: true,
  },
  {
    key: "is_sms_gateway_enable",
    label: "Enable SMS Gateway",
    description: "Enable SMSlenz SMS gateway for sending text messages",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: true,
  },
  {
    key: "sms_gateway_senderid",
    label: "SMS Gateway Sender ID",
    description: "Approved sender ID for SMSlenz SMS gateway (case-sensitive)",
    category: "features",
    defaultValue: "",
    parse: toString,
    isPublic: true,
  },
  {
    key: "currency",
    label: "Currency Code",
    description: "ISO currency code for transactions (e.g., LKR, USD)",
    category: "financial",
    defaultValue: "LKR",
    parse: toString,
    isPublic: true,
  },
  {
    key: "tax_rate",
    label: "Default Tax Rate (%)",
    description: "Default tax percentage applied to sales",
    category: "financial",
    defaultValue: 0,
    parse: toNumber,
    isPublic: true,
  },
  {
    key: "max_discount",
    label: "Maximum Discount (%)",
    description: "Maximum discount percentage allowed on POS",
    category: "financial",
    defaultValue: 100,
    parse: toNumber,
    isPublic: true,
  },
  {
    key: "language",
    label: "Default Language",
    description: "Default UI language code (e.g., en, si, ta)",
    category: "localization",
    defaultValue: "en",
    parse: toString,
    isPublic: true,
  },
  {
    key: "timezone",
    label: "Timezone",
    description: "IANA timezone for date/time operations (e.g., Asia/Colombo)",
    category: "localization",
    defaultValue: "Asia/Colombo",
    parse: toString,
    isPublic: true,
  },
  {
    key: "receipt_footer",
    label: "Receipt Footer Text",
    description: "Custom message printed at bottom of receipts",
    category: "financial",
    defaultValue: "Thank you for your business!",
    parse: toString,
    isPublic: true,
  },
  {
    key: "is_payment_upcoming",
    label: "Show Upcoming Payment Warning",
    description: "Display warnings for upcoming credit payments",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: false,
  },
  {
    key: "is_payment_warning",
    label: "Show Overdue Payment Warning",
    description: "Display warnings for overdue credit payments",
    category: "features",
    defaultValue: false,
    parse: toBool,
    isPublic: false,
  },
  {
    key: "is_terminated",
    label: "Account Terminated",
    description: "System-level flag indicating account termination",
    category: "system",
    defaultValue: false,
    parse: toBool,
    isPublic: false,
  },
  {
    key: "is_enable",
    label: "System Enabled",
    description: "Master enable/disable flag for the entire system",
    category: "system",
    defaultValue: true,
    parse: toBool,
    isPublic: false,
  },
  {
    key: "maintenance_mode",
    label: "Maintenance Mode",
    description: "Enable maintenance mode to restrict access",
    category: "system",
    defaultValue: false,
    parse: toBool,
    isPublic: false,
  },
  {
    key: "cost_code",
    label: "Cost Code Cipher",
    description:
      "10-digit cipher string for cost code generation (maps digits 0-9 to letters)",
    category: "financial",
    defaultValue: "BELOJYNADG",
    parse: toString,
    isPublic: false,
  },
];

export const getConfigDefinition = (key: string) =>
  SYSTEM_CONFIG_REGISTRY.find((f: any) => f.key === key);

export const getConfigByCategory = (category: string) =>
  SYSTEM_CONFIG_REGISTRY.filter((f: any) => f.category === category);

export const getPublicConfigKeys = () =>
  SYSTEM_CONFIG_REGISTRY.filter((f: any) => f.isPublic).map((f: any) => f.key);
